import React, { useCallback, useState } from 'react';
import {
  useForm,
  useFieldArray,
  Controller,
  FormProvider,
} from 'react-hook-form';
// import PasswordInput from '../common/PasswordInput';
import { BtnPrimary } from '../common/Buttons';
import { useThemeStore } from '../../state/theme';
import { zodResolver } from '@hookform/resolvers/zod';
import { agentSchema, FormInputs } from '../../validations/agent';
import { IoAddCircleOutline } from 'react-icons/io5';
import { FaTrashAlt } from 'react-icons/fa';
import PhoneNumberInput from '../common/PhoneNumberInput';
import {
  fluencyOptions,
  languageOptions,
  statusOptions,
  tools,
  channelOptions,
  paymentMethodOptions,
} from '../../constants';
import TagInput from '../common/TagInput';
import SelectComponent, { SelectOption } from '../common/Select';
import {
  FluencyOption,
  PaymentMethod,
  PaymentRecord,
  BonusRecord,
  RecordArrayKey,
  IAgent,
  IAgentBrand,
} from '../../types/agent';
import { fetchBrandList } from '../../hooks/queries';
import { addAgent, updateAgent } from '../../hooks/mutations';
import { MultiValue, SingleValue } from 'react-select';
import { getChangedFields, getFilteredAgentBrands } from '../../utils';
import AgentBrandInfo from './AgentBrandInfo';
import UrlPreview from '../common/UrlPreview';

interface AddAgentProps {
  initialData?: IAgent;
  agentBrands: IAgentBrand[];
  onSuccess: (data: any) => void;
  onError: (error: Error) => void;
  isEditing?: boolean;
}

const AddAgent: React.FC<AddAgentProps> = ({
  initialData,
  agentBrands,
  onSuccess,
  onError,
  isEditing = false,
}) => {
  const { data: brandsData } = fetchBrandList();
  const { mutate: addAgentMutation, isPending: isAddingAgent } = addAgent({
    onSuccess: (data) => onSuccess?.(data),
    onError: (e) => onError?.(e),
  });

  // Simplified brand data state - store the complete brand data with updates
  const [brandDataState, setBrandDataState] = useState<{
    [brandId: string]: IAgentBrand;
  }>(() => {
    const initialBrandData: { [brandId: string]: IAgentBrand } = {};
    if (agentBrands) {
      agentBrands.forEach((brand) => {
        if (brand.agentId === initialData?._id) {
          const brandId =
            typeof brand.brandId === 'object'
              ? (brand.brandId as any)?._id
              : brand.brandId;
          initialBrandData[brandId] = { ...brand };
        }
      });
    }
    return initialBrandData;
  });

  const { mutate: updateAgentMutation, isPending: isUpdatingAgent } =
    updateAgent({
      onSuccess: (data) => onSuccess?.(data),
      onError: (e) => onError?.(e),
    });

  const { isDarkMode } = useThemeStore();

  const form = useForm<FormInputs>({
    resolver: zodResolver(agentSchema),
    defaultValues: {
      ...initialData,
      dob: initialData?.dob || undefined,
      totalExperience: initialData?.totalExperience || '',
    } as any,
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = form;

  const {
    fields: languageFields,
    append: appendLanguage,
    remove: removeLanguage,
  } = useFieldArray({
    control: form.control,
    name: 'languages',
  });

  const {
    fields: pastEmployersFields,
    append: appendPastEmployer,
    remove: removePastEmployer,
  } = useFieldArray({
    control: form.control,
    name: 'pastEmployers',
  });

  // Simplified brand change handler
  const handleBrandFieldChange = (
    brandId: string,
    field: keyof IAgentBrand,
    value: any
  ) => {
    setBrandDataState((prev) => ({
      ...prev,
      [brandId]: {
        ...prev[brandId],
        [field]: value,
      },
    }));
  };

  // Handle nested field changes (like payment.currency)
  const handleNestedBrandFieldChange = (
    brandId: string,
    parentField: keyof IAgentBrand,
    childField: string,
    value: any
  ) => {
    setBrandDataState((prev) => ({
      ...prev,
      [brandId]: {
        ...prev[brandId],
        [parentField]: {
          ...(prev[brandId]?.[parentField] as any),
          [childField]: value,
        },
      },
    }));
  };

  // Add new payment record — routes to the correct backend array based on name
  const addNewPaymentRecord = (brandId: string, name: string = 'Salary Payment') => {
    const today = new Date().toISOString().split('T')[0];

    if (name === 'Bonus') {
      const newBonus: BonusRecord = { name: 'Bonus', effectiveStartDate: today };
      setBrandDataState((prev) => ({
        ...prev,
        [brandId]: {
          ...prev[brandId],
          bonus: [...(Array.isArray(prev[brandId]?.bonus) ? prev[brandId].bonus : []), newBonus],
        },
      }));
      return;
    }

    if (name === 'Others') {
      const newOther: PaymentRecord = {
        name: 'Others',
        toAgent: { currency: 'USD', amount: 0, payType: 'hourly' },
        fromClient: { currency: 'USD', amount: 0, payType: 'hourly' },
        effectiveStartDate: today,
      };
      setBrandDataState((prev) => ({
        ...prev,
        [brandId]: {
          ...prev[brandId],
          otherPayments: [...(prev[brandId]?.otherPayments || []), newOther],
        },
      }));
      return;
    }

    // Salary Payment → payment[]
    setBrandDataState((prev) => {
      const currentPayment = prev[brandId]?.payment;
      const newRecord: PaymentRecord = {
        name,
        toAgent: { currency: 'USD', amount: 0, payType: 'hourly' },
        fromClient: { currency: 'USD', amount: 0, payType: 'hourly' },
        effectiveStartDate: today,
        effectiveEndDate: undefined,
      };

      let updatedPayment: PaymentRecord[];

      if (Array.isArray(currentPayment)) {
        const activeIndex = currentPayment.findIndex((p) => !p.effectiveEndDate);
        const closedPayments = currentPayment.map((p, i) =>
          i === activeIndex ? { ...p, effectiveEndDate: today } : p
        );
        const last = currentPayment[currentPayment.length - 1];
        updatedPayment = [
          ...closedPayments,
          {
            ...newRecord,
            toAgent: { currency: last?.toAgent?.currency || 'USD', amount: 0, payType: last?.toAgent?.payType || 'hourly' },
            fromClient: { currency: last?.fromClient?.currency || 'USD', amount: 0, payType: last?.fromClient?.payType || 'hourly' },
          },
        ];
      } else if (currentPayment) {
        updatedPayment = [
          { ...currentPayment, effectiveEndDate: today } as PaymentRecord,
          {
            ...newRecord,
            toAgent: { currency: (currentPayment as PaymentRecord)?.toAgent?.currency || 'USD', amount: 0, payType: (currentPayment as PaymentRecord)?.toAgent?.payType || 'hourly' },
            fromClient: { currency: (currentPayment as PaymentRecord)?.fromClient?.currency || 'USD', amount: 0, payType: (currentPayment as PaymentRecord)?.fromClient?.payType || 'hourly' },
          },
        ];
      } else {
        updatedPayment = [newRecord];
      }

      return { ...prev, [brandId]: { ...prev[brandId], payment: updatedPayment } };
    });
  };

  // Update date on any record type
  const updatePaymentRecordDate = (
    brandId: string,
    arrayKey: RecordArrayKey,
    recordIndex: number,
    field: 'effectiveStartDate' | 'effectiveEndDate',
    value: string
  ) => {
    setBrandDataState((prev) => {
      if (arrayKey === 'payment') {
        const arr = prev[brandId]?.payment;
        if (!Array.isArray(arr)) return prev;
        const updated = arr.map((p, i) => i === recordIndex ? { ...p, [field]: value || undefined } : p);
        return { ...prev, [brandId]: { ...prev[brandId], payment: updated } };
      }
      const arr = (prev[brandId]?.[arrayKey] as (PaymentRecord | BonusRecord)[]) || [];
      const updated = arr.map((p, i) => i === recordIndex ? { ...p, [field]: value || undefined } : p);
      return { ...prev, [brandId]: { ...prev[brandId], [arrayKey]: updated } };
    });
  };

  // Delete a record from any array
  const deletePaymentRecord = (brandId: string, arrayKey: RecordArrayKey, recordIndex: number) => {
    setBrandDataState((prev) => {
      if (arrayKey === 'bonus') {
        const arr = (Array.isArray(prev[brandId]?.bonus) ? prev[brandId].bonus : []).filter((_, i) => i !== recordIndex);
        return { ...prev, [brandId]: { ...prev[brandId], bonus: arr } };
      }
      if (arrayKey === 'otherPayments') {
        const arr = (prev[brandId]?.otherPayments || []).filter((_, i) => i !== recordIndex);
        return { ...prev, [brandId]: { ...prev[brandId], otherPayments: arr } };
      }
      // payment[]
      const currentPayment = prev[brandId]?.payment;
      if (!Array.isArray(currentPayment) || currentPayment.length <= 1) {
        alert('Cannot delete the only payment record');
        return prev;
      }
      const updatedPayment = currentPayment.filter((_, i) => i !== recordIndex);
      const hasActiveRecord = updatedPayment.some((p) => !p.effectiveEndDate);
      if (!hasActiveRecord && updatedPayment.length > 0) {
        updatedPayment[updatedPayment.length - 1].effectiveEndDate = undefined;
      }
      return { ...prev, [brandId]: { ...prev[brandId], payment: updatedPayment } };
    });
  };

  // Update toAgent on payment[] or otherPayments[]
  const updateToAgentPaymentField = (
    brandId: string,
    arrayKey: 'payment' | 'otherPayments',
    recordIndex: number,
    field: 'amount' | 'currency' | 'payType',
    value: string | number
  ) => {
    setBrandDataState((prev) => {
      if (arrayKey === 'otherPayments') {
        const arr = (prev[brandId]?.otherPayments || []).map((p, i) =>
          i === recordIndex ? { ...p, toAgent: { ...(p.toAgent || {}), [field]: value } } : p
        );
        return { ...prev, [brandId]: { ...prev[brandId], otherPayments: arr } };
      }
      const currentPayment = prev[brandId]?.payment;
      let updatedPayment;
      if (Array.isArray(currentPayment)) {
        updatedPayment = currentPayment.map((p, i) =>
          i === recordIndex ? { ...p, toAgent: { ...(p.toAgent || {}), [field]: value } } : p
        );
      } else {
        updatedPayment = { ...(currentPayment || {}), toAgent: { ...(currentPayment?.toAgent || {}), [field]: value } };
      }
      return { ...prev, [brandId]: { ...prev[brandId], payment: updatedPayment } };
    });
  };

  // Update fromClient on payment[] or otherPayments[]
  const updateFromClientPaymentField = (
    brandId: string,
    arrayKey: 'payment' | 'otherPayments',
    recordIndex: number,
    field: 'amount' | 'currency' | 'payType',
    value: string | number | undefined
  ) => {
    setBrandDataState((prev) => {
      if (arrayKey === 'otherPayments') {
        const arr = (prev[brandId]?.otherPayments || []).map((p, i) =>
          i === recordIndex ? { ...p, fromClient: { ...(p.fromClient || {}), [field]: value } } : p
        );
        return { ...prev, [brandId]: { ...prev[brandId], otherPayments: arr } };
      }
      const currentPayment = prev[brandId]?.payment;
      let updatedPayment;
      if (Array.isArray(currentPayment)) {
        updatedPayment = currentPayment.map((p, i) =>
          i === recordIndex ? { ...p, fromClient: { ...(p.fromClient || {}), [field]: value } } : p
        );
      } else {
        updatedPayment = { ...(currentPayment || {}), fromClient: { ...(currentPayment?.fromClient || {}), [field]: value } };
      }
      return { ...prev, [brandId]: { ...prev[brandId], payment: updatedPayment } };
    });
  };

  // Update a field on a BonusRecord
  const updateBonusRecordField = (
    brandId: string,
    recordIndex: number,
    field: keyof BonusRecord,
    value: string | number
  ) => {
    setBrandDataState((prev) => {
      const arr = (Array.isArray(prev[brandId]?.bonus) ? prev[brandId].bonus : []).map((b, i) =>
        i === recordIndex ? { ...b, [field]: value } : b
      );
      return { ...prev, [brandId]: { ...prev[brandId], bonus: arr } };
    });
  };

  // Update the name field on an otherPayments record
  const updateOtherPaymentName = (brandId: string, recordIndex: number, value: string) => {
    setBrandDataState((prev) => {
      const arr = (prev[brandId]?.otherPayments || []).map((p, i) =>
        i === recordIndex ? { ...p, name: value } : p
      );
      return { ...prev, [brandId]: { ...prev[brandId], otherPayments: arr } };
    });
  };

  // only for breakDetails
  const handleBreakDuration = (
    brandId: string,
    parentField: keyof IAgentBrand,
    childField: string,
    child: string,
    value: string | number
  ) => {
    setBrandDataState((prev) => ({
      ...prev,
      [brandId]: {
        ...prev[brandId],
        [parentField]: {
          ...(prev[brandId]?.[parentField] as any),
          [childField]: {
            ...(prev[brandId]?.[parentField] as any)?.[childField],
            [child]: value,
          },
        },
      },
    }));
  };

  const onSubmit = (data: FormInputs) => {
    const changedData = isEditing ? getChangedFields(data, initialData) : data;

    // Prepare brands data for submission - only include brands that have been modified
    const brandsData = Object.entries(brandDataState)
      .filter(([brandId, brand]) => {
        const original = agentBrands.find((b) => {
          const bBrandId =
            typeof b.brandId === 'object'
              ? (b.brandId as any)?._id
              : b.brandId;
          return bBrandId === brandId && b.agentId === initialData?._id;
        });
        return JSON.stringify(brand) !== JSON.stringify(original);
      })
      .map(([brandId, brand]) => ({
        brandId,
        data: brand,
      }));

    const submissionData = {
      ...(isEditing ? changedData : data),
      ...(brandsData.length > 0 && { brandsData }),
    };

    if (isEditing) {
      updateAgentMutation({
        _id: initialData?._id,
        ...submissionData,
      });
    } else {
      addAgentMutation(submissionData);
    }
  };

  const inputClassName = `w-full rounded-md px-3 py-2 shadow-sm focus:outline-none ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-indigo-500 focus:ring-indigo-500'
      : 'border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
  }`;

  const labelClassName = `block text-sm font-medium mb-1 ${
    isDarkMode ? 'text-gray-300' : 'text-gray-700'
  }`;

  const skillTags = form.watch('skills');

  const onEnterSkill = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const input = e.target as HTMLInputElement;
      const tag = input.value.trim();

      if (tag) {
        const currentTags = form.getValues('skills') || [];
        form.setValue('skills', [...currentTags, tag]);
        input.value = '';
      }
    },
    [form]
  );

  const onRemoveSkill = useCallback(
    (index: number) => {
      const tags = form.getValues('skills') || [];

      form.setValue(
        'skills',
        tags.filter((_: any, i: number) => i !== index)
      );
    },
    [form]
  );

  const filteredAgentBrands = getFilteredAgentBrands(agentBrands, initialData!);
  const getBrandName = (brandId: string) => {
    return brandsData?.brands?.find((b) => b._id === brandId)?.name;
  };

  const resumeUri = form.watch('resumeUri');
  const applicationVideoUri = form.watch('applicationVideoUri');
  const profileUri = form.watch('profileUri');
  const watchedDob = form.watch('dob');
  const formattedDob = watchedDob
    ? new Date(watchedDob).toISOString().split('T')[0]
    : '';

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Personal & Contact Information */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold mb-4 border-b pb-2">
            Personal & Contact Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <label htmlFor="name" className={labelClassName}>
                Name
              </label>
              <input
                id="name"
                type="text"
                {...register('name')}
                className={inputClassName}
              />
              {errors.name && (
                <span className="text-red-500 text-sm">
                  {errors.name.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="dob" className={labelClassName}>
                Date of Birth
              </label>
              <input
                id="dob"
                type="date"
                {...register('dob')}
                value={formattedDob}
                className={inputClassName}
              />
              {errors.dob && (
                <span className="text-red-500 text-sm">
                  {errors.dob.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="personalEmail" className={labelClassName}>
                Personal Email
              </label>
              <input
                id="personalEmail"
                type="email"
                {...register('personalEmail')}
                className={inputClassName}
              />
              {errors.personalEmail && (
                <span className="text-red-500 text-sm">
                  {errors.personalEmail.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="phoneNumber" className={labelClassName}>
                Phone Number
              </label>
              <Controller
                name="phoneNumber"
                control={form.control}
                defaultValue={{
                  number: '',
                  countryCode: '',
                  countryCodeEnum: '',
                }}
                render={({ field: { onChange, value } }) => (
                  <PhoneNumberInput
                    defaultCountry={value.countryCodeEnum || 'us'}
                    value={value.number}
                    onChange={(phone, data) => {
                      onChange?.({
                        number: phone,
                        countryCode: data.country.dialCode,
                        countryCodeEnum: data.country.iso2,
                      });
                    }}
                  />
                )}
              />
              {errors.phoneNumber && (
                <span className="text-red-500 text-sm">
                  {errors.phoneNumber.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="whatsappNumber" className={labelClassName}>
                Whatsapp Number
              </label>
              <Controller
                name="whatsappNumber"
                control={form.control}
                defaultValue={{
                  number: '',
                  countryCode: '',
                  countryCodeEnum: 'us',
                }}
                render={({ field: { onChange, value } }) => (
                  <PhoneNumberInput
                    defaultCountry={value?.countryCodeEnum || 'us'}
                    value={value?.number || ''}
                    onChange={(phone, data) => {
                      onChange?.({
                        number: phone,
                        countryCode: data.country.dialCode,
                        countryCodeEnum: data.country.iso2,
                      });
                    }}
                  />
                )}
              />
              {errors.whatsappNumber && (
                <span className="text-red-500 text-sm">
                  {errors.whatsappNumber.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="profileUri" className={labelClassName}>
                Profile Pic Url
              </label>
              <UrlPreview url={profileUri || ''} />
              {errors.profileUri && (
                <span className="text-red-500 text-sm">
                  {errors.profileUri.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="governmentId" className={labelClassName}>
                Government ID
              </label>
              <input
                id="governmentId"
                type="text"
                {...register('governmentId')}
                className={inputClassName}
              />
              {errors.governmentId && (
                <span className="text-red-500 text-sm">
                  {errors.governmentId.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="resumeUri" className={labelClassName}>
                Resume Url
              </label>

              <UrlPreview url={resumeUri || ''} />
              {errors.resumeUri && (
                <span className="text-red-500 text-sm">
                  {errors.resumeUri.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="applicationVideoUri" className={labelClassName}>
                Application Video Url
              </label>
              <UrlPreview url={applicationVideoUri || ''} />
              {errors.applicationVideoUri && (
                <span className="text-red-500 text-sm">
                  {errors.applicationVideoUri.message}
                </span>
              )}
            </div>
            <div>
              <label htmlFor="currentAddress" className={labelClassName}>
                Current Address
              </label>
              <input
                id="currentAddress"
                type="text"
                {...register('currentAddress')}
                className={inputClassName}
              />
              {errors.currentAddress && (
                <span className="text-red-500 text-sm">
                  {errors.currentAddress.message}
                </span>
              )}
            </div>

            {/* Emergency Contact nested section */}
            <div className="md:col-span-2">
              <h3 className="text-lg font-medium mb-2">Emergency Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <label
                    htmlFor="emergencyContact.name"
                    className={labelClassName}
                  >
                    Emergency Contact Name
                  </label>
                  <input
                    id="emergencyContact.name"
                    type="text"
                    {...register('emergencyContact.name')}
                    className={inputClassName}
                  />
                  {errors.emergencyContact?.name && (
                    <span className="text-red-500 text-sm">
                      {errors.emergencyContact.name.message}
                    </span>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="emergencyContact.phoneNumber"
                    className={labelClassName}
                  >
                    Emergency Contact Number
                  </label>
                  <input
                    id="emergencyContact.phoneNumber"
                    type="text"
                    {...register('emergencyContact.phoneNumber')}
                    className={inputClassName}
                  />
                  {errors.emergencyContact?.phoneNumber && (
                    <span className="text-red-500 text-sm">
                      {errors.emergencyContact.phoneNumber.message}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Professional Details */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold mb-4 border-b pb-2">
            Professional Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <label className={labelClassName}>Skills</label>
              <TagInput
                placeholder="Add skill and press Enter"
                onEnter={onEnterSkill}
                tags={skillTags}
                onRemoveTag={onRemoveSkill}
              />
            </div>

            <div>
              <label htmlFor="status" className={labelClassName}>
                Status
              </label>
              <select
                {...register('status')}
                className={`${inputClassName} appearance-none`}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.status && (
                <span className="text-red-500 text-sm">
                  {errors.status.message}
                </span>
              )}
            </div>

            <div>
              <label className={labelClassName}>Tools</label>
              <Controller
                name="tools"
                control={form.control}
                render={({ field }) => (
                  <SelectComponent
                    {...field}
                    isMulti
                    options={tools}
                    className="w-full"
                    value={tools.filter((option) =>
                      field.value?.includes(option.value)
                    )}
                    onChange={(
                      newValue:
                        | MultiValue<SelectOption>
                        | SingleValue<SelectOption>
                    ) =>
                      field.onChange(
                        (newValue as MultiValue<SelectOption>).map(
                          (item) => item.value
                        )
                      )
                    }
                    placeholder="Select Tools"
                  />
                )}
              />
              {errors.tools && (
                <span className="text-red-500 text-sm">
                  {errors.tools.message}
                </span>
              )}
            </div>

            <div>
              <label className={labelClassName}>Channels</label>
              <Controller
                name="channels"
                control={form.control}
                render={({ field }) => (
                  <SelectComponent
                    {...field}
                    isMulti
                    options={channelOptions}
                    className="w-full"
                    value={channelOptions.filter((option) =>
                      field.value?.includes(option.value)
                    )}
                    onChange={(
                      newValue:
                        | MultiValue<SelectOption>
                        | SingleValue<SelectOption>
                    ) =>
                      field.onChange(
                        (newValue as MultiValue<SelectOption>).map(
                          (item) => item.value
                        )
                      )
                    }
                    placeholder="Select Channels"
                  />
                )}
              />
              {errors.channels && (
                <span className="text-red-500 text-sm">
                  {errors.channels.message}
                </span>
              )}
            </div>
          </div>

          {/* Languages nested section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {languageFields.length > 0 && (
                <label htmlFor="add-language" className={labelClassName}>
                  Languages
                </label>
              )}
              <button
                type="button"
                onClick={() => appendLanguage({ language: '', fluency: [] })}
                className={`flex items-center gap-1 ${
                  isDarkMode
                    ? 'text-white hover:text-indigo-500'
                    : 'text-blue-600 hover:text-blue-800'
                }`}
                id="add-language"
              >
                <IoAddCircleOutline className="h-5 w-5" />
                {languageFields.length <= 0 && 'Add Language'}
              </button>
            </div>

            {languageFields.map((field, index) => (
              <div key={field.id} className="flex items-center gap-2">
                <select
                  {...register(`languages.${index}.language`)}
                  className={`${inputClassName} grow appearance-none`}
                >
                  <option value="">Select Language</option>
                  {languageOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>

                <Controller
                  name={`languages.${index}.fluency`}
                  control={form.control}
                  render={({ field }) => (
                    <SelectComponent<FluencyOption>
                      {...field}
                      isMulti
                      options={fluencyOptions}
                      className="grow w-[500px]"
                      value={fluencyOptions.filter((option) =>
                        field.value?.includes(
                          option.value as 'Spoken' | 'Written'
                        )
                      )}
                      onChange={(
                        newValue:
                          | MultiValue<FluencyOption>
                          | SingleValue<FluencyOption>
                      ) =>
                        field.onChange(
                          (newValue as MultiValue<FluencyOption>).map(
                            (item) => item.value as 'Spoken' | 'Written'
                          )
                        )
                      }
                      placeholder="Select Fluency"
                    />
                  )}
                />

                <button
                  type="button"
                  onClick={() => removeLanguage(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <FaTrashAlt className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>

          {/* Past Employers nested section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {pastEmployersFields.length > 0 && (
                <label className={labelClassName}>Past Employers</label>
              )}
              <button
                type="button"
                onClick={() => {
                  appendPastEmployer({
                    employerName: '',
                    employerUrl: '',
                    startDate: '',
                    endDate: '',
                  });
                }}
                className={`flex items-center gap-1 ${
                  isDarkMode
                    ? 'text-white hover:text-indigo-500'
                    : 'text-blue-600 hover:text-blue-800'
                }`}
              >
                <IoAddCircleOutline className="h-5 w-5" />
                {pastEmployersFields.length <= 0 && 'Add Past Employer'}
              </button>
            </div>

            {pastEmployersFields.map((field, index) => (
              <div key={field.id} className="space-y-3 p-4 border rounded-md">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className={`${labelClassName} text-xs`}>
                      Employer Name
                    </label>
                    <input
                      {...register(`pastEmployers.${index}.employerName`)}
                      type="text"
                      className={inputClassName}
                      placeholder="Company name"
                    />
                    {errors.pastEmployers?.[index]?.employerName && (
                      <span className="text-red-500 text-xs">
                        {errors.pastEmployers[index].employerName?.message}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className={`${labelClassName} text-xs`}>
                      Employer URL
                    </label>
                    <input
                      {...register(`pastEmployers.${index}.employerUrl`)}
                      // type="url"
                      className={inputClassName}
                      placeholder="https://company.com"
                    />
                    {errors.pastEmployers?.[index]?.employerUrl && (
                      <span className="text-red-500 text-xs">
                        {errors.pastEmployers[index].employerUrl?.message}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className={`${labelClassName} text-xs`}>
                      Start Date
                    </label>
                    <input
                      {...register(`pastEmployers.${index}.startDate`)}
                      type="date"
                      className={inputClassName}
                    />
                    {errors.pastEmployers?.[index]?.startDate && (
                      <span className="text-red-500 text-xs">
                        {errors.pastEmployers[index].startDate?.message}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className={`${labelClassName} text-xs`}>
                      End Date
                    </label>
                    <input
                      {...register(`pastEmployers.${index}.endDate`)}
                      type="date"
                      className={inputClassName}
                    />
                    {errors.pastEmployers?.[index]?.endDate && (
                      <span className="text-red-500 text-xs">
                        {errors.pastEmployers[index].endDate?.message}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => removePastEmployer(index)}
                  className="text-red-500 text-sm hover:text-red-700 flex items-center gap-2"
                >
                  <FaTrashAlt className="h-4 w-4" />
                  Remove Employer
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* AgentBrand Details */}
        {isEditing && filteredAgentBrands && filteredAgentBrands.length > 0 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold mb-4 border-b pb-2">
              Brand Details
            </h2>
            {filteredAgentBrands?.map((ab) => {
              const currentBrandData = brandDataState[ab.brandId] || ab;

              return (
                <AgentBrandInfo
                  key={ab.brandId}
                  agentbrand={ab}
                  currentBrand={currentBrandData}
                  getBrandName={getBrandName}
                  handleBreakDuration={handleBreakDuration}
                  updateToAgentPaymentField={updateToAgentPaymentField}
                  updateToClientPaymentField={updateFromClientPaymentField}
                  handleBrandFieldChange={handleBrandFieldChange}
                  handleNestedBrandFieldChange={handleNestedBrandFieldChange}
                  addNewPaymentRecord={addNewPaymentRecord}
                  updatePaymentRecordDate={updatePaymentRecordDate}
                  deletePaymentRecord={deletePaymentRecord}
                  updateBonusRecordField={updateBonusRecordField}
                  updateOtherPaymentName={updateOtherPaymentName}
                />
              );
            })}
          </div>
        )}

        {/* Financial Details */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold mb-4 border-b pb-2">
            Financial Details
          </h2>
          <h3 className="text-lg font-medium mb-2">Bank Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <label htmlFor="bankDetails.name" className={labelClassName}>
                Name as per bank
              </label>
              <input
                id="bankDetails.name"
                type="text"
                {...register('bankDetails.name')}
                className={inputClassName}
              />
              {errors.bankDetails?.name && (
                <span className="text-red-500 text-sm">
                  {errors.bankDetails.name.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="bankDetails.bankName" className={labelClassName}>
                Bank Name
              </label>
              <input
                id="bankDetails.bankName"
                type="text"
                {...register('bankDetails.bankName')}
                className={inputClassName}
              />
              {errors.bankDetails?.bankName && (
                <span className="text-red-500 text-sm">
                  {errors.bankDetails.bankName.message}
                </span>
              )}
            </div>

            <div>
              <label
                htmlFor="bankDetails.accountNumber"
                className={labelClassName}
              >
                Account Number
              </label>
              <input
                id="bankDetails.accountNumber"
                type="text"
                {...register('bankDetails.accountNumber')}
                className={inputClassName}
              />
              {errors.bankDetails?.accountNumber && (
                <span className="text-red-500 text-sm">
                  {errors.bankDetails.accountNumber.message}
                </span>
              )}
            </div>

            <div>
              <label
                htmlFor="bankDetails.accountType"
                className={labelClassName}
              >
                Account Type
              </label>
              <input
                id="bankDetails.accountType"
                type="text"
                {...register('bankDetails.accountType')}
                className={inputClassName}
              />
              {errors.bankDetails?.accountType && (
                <span className="text-red-500 text-sm">
                  {errors.bankDetails.accountType.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="bankDetails.bankCode" className={labelClassName}>
                Bank Code
              </label>
              <input
                id="bankDetails.bankCode"
                type="text"
                {...register('bankDetails.bankCode')}
                className={inputClassName}
              />
              {errors.bankDetails?.bankCode && (
                <span className="text-red-500 text-sm">
                  {errors.bankDetails.bankCode.message}
                </span>
              )}
            </div>

            <div>
              <label
                htmlFor="bankDetails.branchName"
                className={labelClassName}
              >
                Branch Name
              </label>
              <input
                id="bankDetails.branchName"
                type="text"
                {...register('bankDetails.branchName')}
                className={inputClassName}
              />
              {errors.bankDetails?.branchName && (
                <span className="text-red-500 text-sm">
                  {errors.bankDetails.branchName.message}
                </span>
              )}
            </div>
          </div>

          <h3 className="text-lg font-medium mb-2">Payment IDs</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <label htmlFor="paymentIds.paypalId" className={labelClassName}>
                PayPal ID
              </label>
              <input
                id="paymentIds.paypalId"
                type="text"
                {...register('paymentIds.paypalId')}
                className={inputClassName}
              />
              {errors.paymentIds?.paypalId && (
                <span className="text-red-500 text-sm">
                  {errors.paymentIds.paypalId.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="paymentIds.payoneerId" className={labelClassName}>
                Payoneer ID
              </label>
              <input
                id="paymentIds.payoneerId"
                type="text"
                {...register('paymentIds.payoneerId')}
                className={inputClassName}
              />
              {errors.paymentIds?.payoneerId && (
                <span className="text-red-500 text-sm">
                  {errors.paymentIds.payoneerId.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="paymentIds.easypayId" className={labelClassName}>
                EasyPay ID
              </label>
              <input
                id="paymentIds.easypayId"
                type="text"
                {...register('paymentIds.easypayId')}
                className={inputClassName}
              />
              {errors.paymentIds?.easypayId && (
                <span className="text-red-500 text-sm">
                  {errors.paymentIds.easypayId.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="paymentIds.wiseId" className={labelClassName}>
                Wise ID
              </label>
              <input
                id="paymentIds.wiseId"
                type="text"
                {...register('paymentIds.wiseId')}
                className={inputClassName}
              />
              {errors.paymentIds?.wiseId && (
                <span className="text-red-500 text-sm">
                  {errors.paymentIds.wiseId.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="paymentIds.upiId" className={labelClassName}>
                UPI ID
              </label>
              <input
                id="paymentIds.upiId"
                type="text"
                {...register('paymentIds.upiId')}
                className={inputClassName}
              />
              {errors.paymentIds?.upiId && (
                <span className="text-red-500 text-sm">
                  {errors.paymentIds.upiId.message}
                </span>
              )}
            </div>

            <div>
              <label
                htmlFor="bankDetails.bankStatement"
                className={labelClassName}
              >
                Bank Statement
              </label>

              {form.watch('bankDetails.bankStatement') ? (
                <UrlPreview url={form.watch('bankDetails.bankStatement')} />
              ) : (
                <span className="text-gray-500 text-sm">
                  No bank statement uploaded
                </span>
              )}

              {errors.bankDetails?.bankStatement && (
                <span className="text-red-500 text-sm">
                  {errors.bankDetails.bankStatement.message}
                </span>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="preferredPaymentMethod" className={labelClassName}>
              Preferred Payment Method
            </label>
            <Controller
              name="preferredPaymentMethod"
              control={form.control}
              render={({ field }) => (
                <SelectComponent<{
                  value: PaymentMethod;
                  label: string;
                }>
                  {...field}
                  value={paymentMethodOptions.find(
                    (option) => option.value === field.value
                  )}
                  options={paymentMethodOptions}
                  classNamePrefix="react-select"
                  className="w-full"
                  onChange={(
                    newValue:
                      | MultiValue<{ value: PaymentMethod; label: string }>
                      | SingleValue<{ value: PaymentMethod; label: string }>
                  ) =>
                    field.onChange(
                      (
                        newValue as SingleValue<{
                          value: PaymentMethod;
                          label: string;
                        }>
                      )?.value
                    )
                  }
                  placeholder="Select Payment Method"
                  isMulti={false}
                />
              )}
            />
            {errors.preferredPaymentMethod && (
              <span className="text-red-500 text-sm">
                {errors.preferredPaymentMethod.message}
              </span>
            )}
          </div>
        </div>

        {/* Other Information */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold mb-4 border-b pb-2">
            Other Information
          </h2>
          <div>
            <label htmlFor="comments" className={labelClassName}>
              Comments
            </label>
            <textarea
              id="comments"
              {...register('comments')}
              className={inputClassName}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <BtnPrimary disabled={isAddingAgent || isUpdatingAgent} type="submit">
            {isEditing
              ? isUpdatingAgent
                ? 'Updating...'
                : 'Update Agent'
              : isAddingAgent
                ? 'Adding...'
                : 'Add Agent'}
          </BtnPrimary>
        </div>
      </form>
    </FormProvider>
  );
};

export default AddAgent;
